import { useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { getMemberCount, setLastMemberCountPollTime } from '../redux/flows/portal/studio'
import { useAppSelector } from '../redux/store'
import { APIStatus } from '../redux/types'

const POLL_INTERVAL = 300000 // 5 minutes in milliseconds
const MAX_PENDING_TIME = 12000 // 12 seconds (longer than axios timeout)

export const useMemberCount = () => {
  const dispatch = useDispatch()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const pendingStartTimeRef = useRef<number | null>(null)
  const lastDispatchTimeRef = useRef<number>(0)

  const clinicId = useAppSelector((state) => state.studioAuth.studioDetails?.id)
  const pollStatus = useAppSelector((state) => state.studioPortal.memberCountStatus)

  // Ref to access latest pollStatus in callbacks without adding to dependencies
  const pollStatusRef = useRef(pollStatus)
  useEffect(() => {
    pollStatusRef.current = pollStatus
  }, [pollStatus])

  // Track pending state
  useEffect(() => {
    if (pollStatus === APIStatus.pending && pendingStartTimeRef.current === null) {
      pendingStartTimeRef.current = Date.now()
    } else if (pollStatus !== APIStatus.pending) {
      pendingStartTimeRef.current = null
    }
  }, [pollStatus])

  const executePoll = useCallback(() => {
    if (!clinicId) return

    try {
      const now = Date.now()
      const timeSinceLastDispatch = now - lastDispatchTimeRef.current

      // Prevent dispatching too frequently (safety check)
      if (timeSinceLastDispatch < 1000) {
        return
      }

      const currentPollStatus = pollStatusRef.current

      // Check if stuck in pending
      if (currentPollStatus === APIStatus.pending) {
        const pendingDuration = pendingStartTimeRef.current ? now - pendingStartTimeRef.current : 0

        if (pendingDuration > MAX_PENDING_TIME) {
          console.error(`[MemberCountPoll] Request STUCK in pending for ${pendingDuration}ms - forcing recovery`)
          pendingStartTimeRef.current = null
          // Continue to dispatch new request
        } else {
          return
        }
      }

      lastDispatchTimeRef.current = now
      dispatch(setLastMemberCountPollTime(now))

      dispatch(
        getMemberCount({
          clinicId,
        }) as any,
      )
    } catch (error) {
      pendingStartTimeRef.current = null
    }
  }, [clinicId, dispatch])

  const fetchAllMemberCount = useCallback(() => {
    dispatch(getMemberCount({}) as any)
  }, [dispatch])

  // Initial load
  useEffect(() => {
    if (!clinicId) return

    executePoll()
    fetchAllMemberCount()
  }, [clinicId, executePoll, fetchAllMemberCount])

  // Polling loop
  useEffect(() => {
    if (!clinicId) {
      return
    }

    // Set up interval
    intervalRef.current = setInterval(() => {
      executePoll()
    }, POLL_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      pendingStartTimeRef.current = null
    }
  }, [clinicId, executePoll, fetchAllMemberCount])

  // Handle tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && clinicId) {
        // Force a poll when tab becomes visible
        pendingStartTimeRef.current = null
        executePoll()
        fetchAllMemberCount()
      } else if (document.visibilityState === 'hidden') {
        // Tab is hidden, polling will continue in background
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [clinicId, executePoll, fetchAllMemberCount])

  // Handle network reconnection
  useEffect(() => {
    const handleOnline = () => {
      pendingStartTimeRef.current = null
      executePoll()
    }

    const handleOffline = () => {
      // console.log('[MemberCountPoll] Network disconnected')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [clinicId, executePoll, fetchAllMemberCount])
}
